import { TriangleAlert, X } from "@icons";
import classNames from "classnames";
import {
  ClipboardEvent,
  KeyboardEvent,
  useMemo,
  useRef,
  useState,
} from "react";

import FieldLabel from "../_shared/field-label";
import FieldMessage from "../_shared/field-message";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface TaggedInputProps {
  /** Committed recipient emails (controlled). */
  value: string[];
  /** Fires with the next full list whenever a pill is added or removed. */
  onChange: (next: string[]) => void;
  /**
   * Emails to render as invalid/undeliverable pills (e.g. server-side bounces).
   * They still count as part of `value`; matched case-insensitively.
   */
  invalidEmails?: string[];
  label?: string;
  /** Description surfaced behind the (i) toggle next to the label. */
  info?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  /** Hard cap on recipients — the field disables and refuses more once reached. */
  max?: number;
  /** Pills past this count collapse into a "+N more" toggle. */
  maxChips?: number;
  /** When set, only addresses ending in one of these domains are accepted. */
  allowedDomains?: string[];
  /** External/server error message (shown when there is no live draft error). */
  error?: string;
  /** Muted fallback note shown while the field is empty (e.g. the routing default). */
  defaultBadge?: string;
  name?: string;
  ariaLabel?: string;
  invalidEmailMessage?: string;
  duplicateMessage?: (email: string) => string;
  blockedDomainMessage?: string;
  limitReachedMessage?: (max: number) => string;
}

function normalize(email: string): string {
  return email.trim().toLowerCase();
}

function isValidEmail(email: string): boolean {
  return EMAIL_REGEX.test(email);
}

export function TaggedInput({
  value,
  onChange,
  invalidEmails,
  label,
  info,
  placeholder = "Add an email…",
  disabled = false,
  required = false,
  max,
  maxChips = 6,
  allowedDomains,
  error,
  defaultBadge,
  name,
  ariaLabel,
  invalidEmailMessage = "Enter a valid email address.",
  duplicateMessage = (email) => `“${email}” is already a recipient.`,
  blockedDomainMessage = "Personal email domains aren’t allowed. Use a work address.",
  limitReachedMessage = (limit) =>
    `You’ve reached the ${limit}-recipient limit.`,
}: TaggedInputProps) {
  const [draft, setDraft] = useState("");
  const [draftError, setDraftError] = useState<string | null>(null);
  const [shake, setShake] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const invalidSet = useMemo(
    () => new Set((invalidEmails ?? []).map(normalize)),
    [invalidEmails],
  );

  const atLimit = max != null && value.length >= max;
  const displayError = draftError ?? error ?? null;
  const hasError = !!displayError;

  const visibleChips = expanded ? value : value.slice(0, maxChips);
  const hiddenCount = value.length - visibleChips.length;

  function flashReject() {
    setShake(true);
    window.setTimeout(() => setShake(false), 340);
  }

  function commit(raw: string): boolean {
    const candidate = normalize(raw.replace(/,+$/, ""));
    if (!candidate) return true;
    if (!isValidEmail(candidate)) {
      setDraftError(invalidEmailMessage);
      flashReject();
      return false;
    }
    if (value.some((email) => normalize(email) === candidate)) {
      setDraftError(duplicateMessage(candidate));
      flashReject();
      return false;
    }
    if (
      allowedDomains &&
      !allowedDomains.some((domain) =>
        candidate.endsWith(`@${domain.toLowerCase()}`),
      )
    ) {
      setDraftError(blockedDomainMessage);
      flashReject();
      return false;
    }
    if (atLimit) {
      setDraftError(limitReachedMessage(max as number));
      flashReject();
      return false;
    }
    onChange([...value, candidate]);
    setDraft("");
    setDraftError(null);
    return true;
  }

  function removeAt(index: number) {
    onChange(value.filter((_, i) => i !== index));
    setDraftError(null);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      commit(draft);
      return;
    }
    if (event.key === "Backspace" && draft === "" && value.length > 0) {
      removeAt(value.length - 1);
    }
  }

  function handleBlur() {
    if (draft.trim()) commit(draft);
  }

  function handlePaste(event: ClipboardEvent<HTMLInputElement>) {
    const pasted = event.clipboardData.getData("text");
    const tokens = pasted
      .split(/[\s,;]+/)
      .map((token) => normalize(token))
      .filter(Boolean);
    if (tokens.length <= 1) return;

    event.preventDefault();
    const additions: string[] = [];
    let rejected: string | null = null;
    for (const token of tokens) {
      if (max != null && value.length + additions.length >= max) {
        rejected = limitReachedMessage(max);
        break;
      }
      if (!isValidEmail(token)) {
        rejected = invalidEmailMessage;
        continue;
      }
      if (
        allowedDomains &&
        !allowedDomains.some((domain) =>
          token.endsWith(`@${domain.toLowerCase()}`),
        )
      ) {
        rejected = blockedDomainMessage;
        continue;
      }
      if (!value.includes(token) && !additions.includes(token))
        additions.push(token);
    }
    if (additions.length > 0) onChange([...value, ...additions]);
    setDraft("");
    setDraftError(rejected);
    if (rejected) flashReject();
  }

  return (
    <div className="font-sans">
      <FieldLabel
        htmlFor={name}
        label={label}
        required={required}
        info={info}
      />

      <div
        role="group"
        aria-label={ariaLabel ?? label}
        onClick={() => {
          if (!disabled) inputRef.current?.focus();
        }}
        className={classNames(
          "flex flex-wrap items-center gap-1.5 min-h-10 px-2 py-[5px] rounded border bg-white transition-[border-color,box-shadow]",
          {
            "border-gray-200 cursor-text": !hasError && !disabled,
            "focus-within:border-primary-300 focus-within:ring-2 focus-within:ring-primary-300/40":
              !hasError && !disabled,
            "border-red-500 ring-2 ring-red-500/20": hasError,
            "border-gray-200 bg-gray-100 cursor-not-allowed": disabled,
            "motion-safe:animate-shake": shake,
          },
        )}
      >
        <input
          ref={inputRef}
          id={name}
          name={name}
          type="email"
          value={draft}
          placeholder={placeholder}
          disabled={disabled || atLimit}
          aria-invalid={hasError}
          className="flex-1 min-w-[110px] h-7 px-1 border-0 outline-none bg-transparent text-sm text-gray-800 placeholder:text-gray-400 disabled:cursor-not-allowed"
          onChange={(event) => {
            setDraft(event.target.value);
            if (draftError) setDraftError(null);
          }}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onPaste={handlePaste}
        />
      </div>

      {value.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
          {visibleChips.map((email, index) => {
            const invalid = invalidSet.has(normalize(email));
            return (
              <span
                key={`${email}-${index}`}
                data-testid="tagged-input-chip"
                className={classNames(
                  "inline-flex items-center gap-1.5 h-[26px] pl-2.5 pr-1 rounded-full text-[13px] font-medium max-w-full",
                  {
                    "bg-primary-50 text-primary-700": !invalid && !disabled,
                    "bg-red-200 text-red-800": invalid && !disabled,
                    "bg-gray-200 text-gray-500": disabled,
                  },
                )}
              >
                {invalid && (
                  <span className="flex shrink-0 -ml-[3px] text-red-500">
                    <TriangleAlert size={13} />
                  </span>
                )}
                <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                  {email}
                </span>
                {!disabled && (
                  <button
                    type="button"
                    aria-label={`Remove ${email}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      removeAt(index);
                    }}
                    className={classNames(
                      "inline-flex items-center justify-center w-[18px] h-[18px] shrink-0 rounded-full border-0 bg-transparent cursor-pointer transition-colors",
                      {
                        "text-primary-400 hover:bg-primary-100 hover:text-primary-700":
                          !invalid,
                        "text-red-500 hover:bg-red-100": invalid,
                      },
                    )}
                  >
                    <X size={12} />
                  </button>
                )}
              </span>
            );
          })}
          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="inline-flex items-center h-[26px] px-2.5 rounded-full border border-dashed border-gray-300 bg-transparent text-xs font-medium text-gray-500 cursor-pointer transition-colors hover:bg-gray-50 hover:text-gray-700 hover:border-gray-400"
            >
              {`+${hiddenCount} more`}
            </button>
          )}
          {expanded && value.length > maxChips && (
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="inline-flex items-center h-[26px] px-2.5 rounded-full border border-dashed border-gray-300 bg-transparent text-xs font-medium text-gray-500 cursor-pointer transition-colors hover:bg-gray-50 hover:text-gray-700 hover:border-gray-400"
            >
              Show less
            </button>
          )}
        </div>
      )}

      <FieldMessage
        error={displayError}
        defaultBadge={defaultBadge}
        isEmpty={value.length === 0}
      />
    </div>
  );
}

export default TaggedInput;
